import React, { useState, useCallback, useMemo } from 'react';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import { CategoryHeader } from './CategoryHeader';
import { ChannelRow } from './ChannelRow';
import { PriceRow } from './PriceRow';
import { SvodRow } from './SvodRow';
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import DtvText, { IDtvTextProps } from '@directv-digital/dds.ui.dtv-text';

interface AccordionRowsProps {
    index: number;
    section?: {
        accordionHeading?: any;
        accordionType?: string;
        expand?: boolean;
        accordionDetails?: any[];
        nestedSections?: any[];
        packageId?: string;
        accordionLabel?: string;
    };
    displayPackages?: any[];
    activePackagesByColumn?: any[];
    mainColWidthStyle?: React.CSSProperties;
    accLength?: number;
    headerHeight?: string | number;
    myfreeDtvDescription?: IDtvTextProps;
    type?: string;
    getFixedCol?: any;
    expand?: boolean;
    changeRowPackages?: any[];
    onCloseChangeRow?: () => void;
    genrePackages?: any[];
    allPackages?: any[];
    priceMap?: Record<string, any>;
}

export const AccordionRows = React.memo((props: AccordionRowsProps) => {
  try {
    const {
      index,
      section = {},
      displayPackages = [],
      activePackagesByColumn: _activePackagesByColumn = [],
      mainColWidthStyle: _mainColWidthStyle,
      accLength = 0,
      headerHeight,
      myfreeDtvDescription,
      type = "parent",
      getFixedCol
    } = props;

    const accHeading = section?.accordionHeading?.content || "Category Header";

    const initialAccState = useMemo(() => (
      Array.from({ length: accLength }, () => ({
        expanded: type !== "nested" ? section?.expand ?? false : false
      }))
    ), [accLength, section?.expand, type]);

    const [accState, setAccState] = useState<any>(initialAccState);

    const scrollOntoStickyHeader = useCallback((id: string) => {
      const accSummaryElem = document.getElementById(id);
      if (accSummaryElem && accSummaryElem.offsetTop > 0) {
        window.scrollBy({
          top: -accSummaryElem.offsetTop,
          behavior: "smooth"
        });
      }
    }, []);

    // Toggle accordion expand/collapse
    const handleCategoryHeaderClick = useCallback((clickedIdx: number) => {
      setAccState((prevAcc: any[]) => prevAcc.map((item, i) => (i === clickedIdx ? {
        ...item,
        expanded: !item.expanded
      } : item)));
      scrollOntoStickyHeader(`caqt-accordion-summary-${type}-${clickedIdx}`);
    }, [type, scrollOntoStickyHeader]);

    if (!section || typeof section !== 'object') {
      return <ErrorDisplay component="AccordionRows" error={"Invalid section data"} />;
    }

    return (
            <Accordion
                expanded={!!accState?.[index]?.expanded}
                id={`caqt-section-accordion-${accHeading}-${index}`}
                key={`caqt-section-accordion-${accHeading}-${index}`}
                TransitionProps={{
 timeout: 400,
unmountOnExit: true
}}
                sx={{
                    backgroundColor: "transparent !important",
                    margin: "0px !important",
                    borderBottom: `1px solid ${colorPalette.neutral[300]}`,
                    boxShadow: "none",
                    "&::before": {
                        display: "none"
                    }
                }}
            >
                <CategoryHeader
                    accordionHeading={section?.accordionHeading}
                    ariaControls={`caqt-accordion-summary-${type}-${index}`}
                    expanded={!!accState?.[index]?.expanded}
                    handleClick={handleCategoryHeaderClick}
                    headerHeight={headerHeight}
                    id={`caqt-accordion-summary-${type}-${index}`}
                    index={index}
                />
                <AccordionDetails
                    id={`caqt-accordion-details-${type}-${index}`}
                    key={`caqt-accordion-details-${type}-${index}`}
                    sx={{
                        padding: "0px",
margin: "0px",
                        maxWidth: "100% !important",
                        fontFamily: "roboto-regular",
                        backgroundColor: colorPalette?.neutral_white?.main
                    }}
                >
                    {section?.accordionType === "price" && (
                        <PriceRow
                            accIndex={index}
                            displayPackages={displayPackages}
                            id={`caqt-accordion-details-offers-${accHeading}-${index}`}
                            key={`caqt-accordion-details-offers-${accHeading}-${index}`}
                            priceData={section?.accordionDetails}
                        />
                    )}

                    {section?.accordionType === "channels" && Array.isArray(section?.nestedSections) && section.nestedSections.map((nestedSection, nestedIdx) => (
                        <React.Fragment key={`nested-section-${nestedIdx}`}>
                            <DtvBox
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    padding: "12px 20px",
                                    background: colorPalette.blue[50]
                                }}
                            >
                                <DtvText
                                    content={nestedSection.label}
                                    variant={"body3medium"}
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "16px"
                                    }}
                                />
                            </DtvBox>
                            {nestedSection.type === "svod" && Array.isArray(nestedSection.rows) && nestedSection.rows.map((svod, svodIdx) => (
                                <SvodRow
                                    isFirst={svodIdx === 0}
                                    key={`svod-row-${svodIdx}`}
                                    svod={svod}
                                />
                            ))}
                            {nestedSection.type === "channels" && Array.isArray(nestedSection.rows) && nestedSection.rows.map((channel, chIdx) => (
                                <ChannelRow
                                    category={channel?.category}
                                    channelDetails={channel}
                                    getFixedCol={getFixedCol}
                                    index={chIdx}
                                    indicators={channel?.indicators}
                                    key={`channel-row-${chIdx}`}
                                    myfreeDtvDescription={myfreeDtvDescription}
                                    name={channel?.channelName}
                                    packageId={section?.packageId}
                                    packagesList={displayPackages}
                                />
                            ))}
                        </React.Fragment>
                    ))}

                    {/* Flat Channels accordion (e.g. MyFree DTV) — renders rows directly from accordionDetails */}
                    {section?.accordionType === "Channels" && section?.accordionDetails?.[0]?.row?.map((channel, chIdx) => (
                        <ChannelRow
                            category={channel?.category}
                            channelDetails={channel}
                            getFixedCol={getFixedCol}
                            index={chIdx}
                            indicators={channel?.indicators}
                            key={`flat-channel-row-${chIdx}`}
                            myfreeDtvDescription={myfreeDtvDescription}
                            name={channel?.channelName}
                            packageId={section?.packageId}
                            packagesList={displayPackages}
                        />
                    ))}
                </AccordionDetails>
            </Accordion>
    );
  } catch (error) {
    console.error("Error in Customer Acq Compare Tool - AccordionRows >> ", error);
    return <ErrorDisplay component="Customer Acq Compare Tool -> AccordionRows" error={error} />;
  }
});
